import React from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export function PageHeading({ className, children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
	return (
		<h1
			className={cn("page-heading text-2xl font-semibold leading-snug tracking-tight text-foreground", className)}
			{...props}
		>
			{children}
		</h1>
	);
}
export function PageHeader({
	title,
	description,
	actions,
}: {
	title: React.ReactNode;
	description?: React.ReactNode;
	actions?: React.ReactNode;
}) {
	return (
		<header className="page-header flex flex-wrap items-start justify-between gap-4">
			<div className="min-w-0 space-y-1">
				<PageHeading>{title}</PageHeading>
				{description && (
					<p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">{description}</p>
				)}
			</div>
			{actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
		</header>
	);
}
export function FilterBar({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn(
				"filter-bar flex flex-wrap items-center gap-3 rounded-[var(--radius-surface)] border border-border/60 bg-card p-4",
				className,
			)}
			{...props}
		/>
	);
}
export function PageState({
	title,
	description,
	action,
	error = false,
}: {
	title: React.ReactNode;
	description?: React.ReactNode;
	action?: React.ReactNode;
	error?: boolean;
}) {
	return (
		<div
			role={error ? "alert" : "status"}
			className="flex min-h-48 flex-col items-center justify-center gap-3 px-6 py-10 text-center"
		>
			<p className={cn("text-base font-semibold", error && "text-destructive")}>{title}</p>
			{description && <p className="max-w-md text-sm text-muted-foreground">{description}</p>}
			{action}
		</div>
	);
}

/** Compact summaries and definition rows shared by operational workspaces. */
export function MetricCard({
	label,
	value,
	hint,
	icon: Icon,
	tone = "info",
}: {
	label: React.ReactNode;
	value: React.ReactNode;
	hint?: React.ReactNode;
	icon?: LucideIcon;
	tone?: "info" | "success" | "warning" | "violet" | "orange" | "danger";
}) {
	return (
		<div className="metric-card" data-tone={tone}>
			<div className="flex items-center justify-between gap-3">
				<p className="text-sm text-muted-foreground">{label}</p>
				{Icon && (
					<span className="metric-icon">
						<Icon className="h-5 w-5" aria-hidden />
					</span>
				)}
			</div>
			<div className="mt-3 break-words text-2xl font-semibold tabular-nums">{value}</div>
			{hint && <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{hint}</p>}
		</div>
	);
}

export function DataRows({ rows }: { rows: { label: React.ReactNode; value: React.ReactNode; total?: boolean }[] }) {
	return (
		<dl className="data-rows">
			{rows.map((row, index) => (
				<div key={index} className={row.total ? "data-row data-row-total" : "data-row"}>
					<dt>{row.label}</dt>
					<dd>{row.value}</dd>
				</div>
			))}
		</dl>
	);
}
